import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import apiClient from "../api/client";
import { Eye, EyeOff, Mail, Lock, Users, User, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import InlineNotice from './common/InlineNotice';


const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    user_type: 'single',
    two_fa_code: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState(null);
  
  // Email verification state
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | error
  const [resendError, setResendError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  
  // Change email flow state
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [changeEmailStatus, setChangeEmailStatus] = useState('idle'); // idle | sending | sent | error
  const [changeEmailError, setChangeEmailError] = useState('');
  
  // 18+ age confirmation state
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [ageTouched, setAgeTouched] = useState(false);
  
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const safeInternalPath = (p) => {
    if (!p || typeof p !== 'string') return null;
    if (!p.startsWith('/')) return null;
    if (p.startsWith('//')) return null;
    return p;
  };

  const consumePostAuthRedirect = () => {
    try {
      const raw = localStorage.getItem('post_auth_redirect');
      if (raw) localStorage.removeItem('post_auth_redirect');
      return safeInternalPath(raw);
    } catch (e) {
      return null;
    }
  };

  const peekPostAuthRedirect = () => {
    try {
      const raw = localStorage.getItem('post_auth_redirect');
      return safeInternalPath(raw);
    } catch (e) {
      return null;
    }
  };

  // Show session expired message if redirected with reason
  useEffect(() => {
    const reason = searchParams.get('reason');
    const verified = searchParams.get('verified');
    
    if (reason === 'session_expired') {
      // Show toast once and clear the param
      toast.info('Your session expired. Please sign in again.', {
        duration: 5000,
        id: 'session-expired', // Prevent duplicates
      });
      // Remove the reason param to prevent showing again on refresh
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
    
    if (verified === '1') {
      toast.success('Email verified! You can now sign in.', {
        duration: 5000,
        id: 'email-verified',
      });
      searchParams.delete('verified');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Capture referral code from URL or localStorage
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refFromUrl = params.get('ref');
    const refFromStorage = localStorage.getItem('ref_code');
    const mode = params.get('mode');

    // Set the referral code (URL takes precedence)
    const finalRef = refFromUrl || refFromStorage;
    if (finalRef) {
      setReferralCode(finalRef);
      console.log('[AuthPage] Captured referral code:', finalRef);
    }

    // Set login/signup mode based on URL param
    if (mode === 'signup') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [location]);

  // Cooldown timer for resend verification
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    // Reset verification UI on input change
    setNeedsVerify(false);
    setResendStatus('idle');
    setResendError('');
  };

  // Handle resend verification email
  const handleResendVerify = async () => {
    const email = (formData.email || '').trim().toLowerCase();
    if (!email) return;

    setResendStatus('sending');
    setResendError('');

    try {
      await apiClient.post(`/auth/send-verification`, { email });
      setResendStatus('sent');
      setCooldown(60);
    } catch (e) {
      setResendStatus('error');
      setResendError(e?.response?.data?.detail || 'Could not resend verification email.');
    }
  };

  // Handle change email for typo correction
  const handleChangeEmail = async () => {
    const currentEmail = (formData.email || '').trim().toLowerCase();
    const password = formData.password || '';
    const newEmailVal = (newEmail || '').trim().toLowerCase();
    
    if (!currentEmail || !password || !newEmailVal) {
      setChangeEmailError('Please fill in all fields');
      return;
    }
    
    if (currentEmail === newEmailVal) {
      setChangeEmailError('New email must be different');
      return;
    }

    setChangeEmailStatus('sending');
    setChangeEmailError('');

    try {
      await apiClient.post(`/auth/change-unverified-email`, {
        current_email: currentEmail,
        new_email: newEmailVal,
        password: password
      });
      setChangeEmailStatus('sent');
      // Update the form with new email
      setFormData(prev => ({ ...prev, email: newEmailVal }));
      setShowChangeEmail(false);
      setNewEmail('');
      // Trigger cooldown for resend
      setCooldown(60);
    } catch (e) {
      setChangeEmailStatus('error');
      setChangeEmailError(e?.response?.data?.detail || 'Could not change email.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate 18+ confirmation for signup
    if (!isLogin && !ageConfirmed) {
      setAgeTouched(true);
      setError("You must confirm you're 18+ to create an account.");
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const response = await apiClient.post(`/auth/login`, {
          email: formData.email,
          password: formData.password,
          two_fa_code: formData.two_fa_code || undefined
        });
        
        // A4: Login flow - await login to complete session setup before navigation
        await login(response.data.access_token, response.data.refresh_token);
        // If we came from an invite (or other deep link), return there.
        const redirectTo = consumePostAuthRedirect();
        navigate(redirectTo || '/dashboard', { replace: true });
      } else {
        // Build registration payload
        const registrationPayload = {
          email: formData.email,
          password: formData.password,
          user_type: formData.user_type,
          age_confirmed: ageConfirmed === true  // Server-side 18+ enforcement
        };

        // Include referral code if available
        if (referralCode) {
          registrationPayload.referral_code = referralCode;
          console.log('[AuthPage] Including referral code in registration:', referralCode);
        }

        // SEO attribution: include the page the user was on when they clicked
        // the CTA (set by PublicSEOHeader / GradientHero / BottomCTA).
        try {
          const seoReferrer = sessionStorage.getItem('ul_seo_referrer_path');
          if (seoReferrer) {
            registrationPayload.referrer_path = seoReferrer;
          }
        } catch {
          // sessionStorage unavailable — skip
        }

        await apiClient.post(`/auth/register`, registrationPayload);

        // Clear referral code + SEO referrer from storage after successful registration
        localStorage.removeItem('ref_code');
        try { sessionStorage.removeItem('ul_seo_referrer_path'); } catch {
          // sessionStorage unavailable — skip
        }
        
        // Auto-login after successful registration
        const loginResponse = await apiClient.post(`/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        // A4: Login flow - await login to complete session setup before navigation
        await login(loginResponse.data.access_token, loginResponse.data.refresh_token);
        // New users must complete profile setup first. If there's a post-auth target,
        // carry it forward so profile setup can return them.
        const redirectTo = peekPostAuthRedirect();
        if (redirectTo) {
          try {
            localStorage.setItem('post_profile_setup_redirect', redirectTo);
          } catch (e) {}
        }
        navigate('/profile-setup', { replace: true });
      }
    } catch (error) {
      console.error('Auth error:', error);
      const errorCode = error.response?.data?.detail;
      const retryAfter = error.response?.headers?.['retry-after'];
      
      // Map backend error codes to user-friendly messages
      let userMessage;
      switch (errorCode) {
        case 'USER_NOT_FOUND':
          userMessage = "We couldn't find an account with that email. Check for typos or create a new account.";
          break;
        case 'NO_PASSWORD':
          userMessage = "This account was created with social login. Try signing in with Google instead.";
          break;
        case 'WRONG_PASSWORD':
          userMessage = "That password isn't right. Try again or use 'Forgot password' below.";
          break;
        case 'BANNED':
          userMessage = "This account has been banned. Contact support if you believe this is an error.";
          break;
        case '2FA_REQUIRED':
          userMessage = "Please enter your 2FA code to continue.";
          break;
        case '2FA_INVALID':
          userMessage = "That 2FA code isn't valid. Check your authenticator app and try again.";
          break;
        case 'EMAIL_NOT_VERIFIED':
          userMessage = "Your email isn't verified yet. Check your inbox or resend the verification link below.";
          setNeedsVerify(true);
          break;
        case 'TOO_MANY_ATTEMPTS':
          const minutes = retryAfter ? Math.ceil(parseInt(retryAfter) / 60) : 15;
          userMessage = `Too many login attempts. Please wait ${minutes} minutes before trying again.`;
          break;
        default:
          // Handle suspended accounts (format: "SUSPENDED:2026-01-10 15:00 UTC")
          if (errorCode?.startsWith('SUSPENDED:')) {
            const suspendedUntil = errorCode.replace('SUSPENDED:', '');
            userMessage = `Your account is temporarily suspended until ${suspendedUntil}. Contact support for more info.`;
          } else {
            userMessage = error.response?.data?.detail || 
              "Something didn't work. Double-check your email and password, or try again in a moment.";
          }
      }
      
      setError(userMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center justify-center space-x-2 mb-8">
            <Heart className="w-8 h-8 text-rose-600 fill-current" />
            <span className="font-playfair text-2xl font-bold text-gray-900">
              Unity Love
            </span>
          </div>

          <div className="glass rounded-2xl p-8 slide-up">
            <div className="text-center mb-8">
              <h2 className="font-playfair text-3xl font-bold text-gray-900 mb-2">
                {isLogin ? 'Welcome Back' : 'Join Our Community'}
              </h2>
              <p className="text-gray-600">
                {isLogin 
                  ? 'Sign in to continue your journey' 
                  : 'Create your account to get started'
                }
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <InlineNotice
                  variant="error"
                  testId="auth-error-notice"
                >
                  {error}
                </InlineNotice>
              )}

              {/* Email Verification Banner */}
              {needsVerify && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="verify-email-banner">
                  <div className="font-semibold text-gray-900">Verify your email</div>
                  <p className="text-sm text-gray-600 mt-1">
                    Your account exists, but the email isn't verified yet. Check your inbox — or resend the verification link.
                  </p>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleResendVerify}
                      disabled={resendStatus === 'sending' || cooldown > 0}
                      className="px-4 py-2 rounded-xl bg-gray-900 text-white font-semibold disabled:opacity-60 hover:bg-gray-800 transition-colors"
                      data-testid="resend-verify-btn"
                    >
                      {cooldown > 0
                        ? `Resend in ${cooldown}s`
                        : resendStatus === 'sending'
                          ? 'Sending...'
                          : 'Resend verification'}
                    </button>

                    {resendStatus === 'sent' && (
                      <span className="text-sm text-emerald-600 font-semibold">Sent ✅</span>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => setShowChangeEmail(!showChangeEmail)}
                      className="text-sm text-gray-600 underline hover:text-gray-900"
                    >
                      {showChangeEmail ? 'Cancel' : 'Wrong email?'}
                    </button>
                  </div>

                  {resendStatus === 'error' && (
                    <p className="text-sm text-red-600 mt-2">{resendError}</p>
                  )}
                  
                  {/* Change Email Form */}
                  {showChangeEmail && (
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <p className="text-sm text-gray-700 mb-2">
                        Typo in your email? Enter the correct one below:
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="New email address"
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-gray-900 text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                        />
                        <button
                          type="button"
                          onClick={handleChangeEmail}
                          disabled={changeEmailStatus === 'sending' || !newEmail}
                          className="px-4 py-2 rounded-lg bg-amber-600 text-white font-semibold disabled:opacity-60 hover:bg-amber-700 transition-colors text-sm"
                        >
                          {changeEmailStatus === 'sending' ? 'Updating...' : 'Update & Resend'}
                        </button>
                      </div>
                      {changeEmailStatus === 'sent' && (
                        <p className="text-sm text-emerald-600 mt-2">Email updated! Check your new inbox.</p>
                      )}
                      {changeEmailStatus === 'error' && (
                        <p className="text-sm text-red-600 mt-2">{changeEmailError}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Your password is required to confirm this change.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* User Type Selection (Register only) */}
              {!isLogin && (
                <div>
                  <label className="form-label">I am a</label>
                  <div className="grid grid-cols-1 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, user_type: 'couple'})}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        formData.user_type === 'couple'
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                      data-testid="couple-type-btn"
                    >
                      <Users className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">Couple</div>
                        <div className="text-sm text-gray-600">Looking for single women to join us</div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, user_type: 'single'})}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        formData.user_type === 'single'
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                      data-testid="single-type-btn"
                    >
                      <User className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">Single Woman</div>
                        <div className="text-sm text-gray-600">Interested in joining established couples</div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, user_type: 'poly_single'})}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        formData.user_type === 'poly_single'
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                      data-testid="poly-single-type-btn"
                    >
                      <Heart className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">Looking to Start Poly Family</div>
                        <div className="text-sm text-gray-600">Single person seeking to build polyamorous relationships</div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, user_type: 'mono_single'})}
                      className={`p-4 border-2 rounded-xl flex items-center space-x-3 transition-all ${
                        formData.user_type === 'mono_single'
                          ? 'border-rose-500 bg-rose-50 text-rose-700'
                          : 'border-gray-200 hover:border-rose-300'
                      }`}
                      data-testid="mono-single-type-btn"
                    >
                      <User className="w-5 h-5" />
                      <div className="text-left">
                        <div className="font-medium">Single (Traditional Dating)</div>
                        <div className="text-sm text-gray-600">Monogamous single seeking one-on-one relationships</div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Email Address */}
              <label className="block mb-3">
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </span>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Mail className="h-4 w-4 text-rose-500" />
                  </span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    autoComplete="email"
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="Enter your email"
                    required
                    data-testid="email-input"
                  />
                </div>
              </label>

              {/* Password */}
              <label className="block mb-3">
                <span className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </span>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <Lock className="h-4 w-4 text-rose-500" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="Enter your password"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                  </button>
                </div>
              </label>

              {/* Forgot Password Link (Login only) */}
              {isLogin && (
                <div className="flex justify-end -mt-3 mb-1">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-rose-600 hover:text-rose-700 font-medium"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
              )}

              {/* 2FA Code (Login only, if needed) */}
              {isLogin && (
                <label className="block mb-3">
                  <span className="block text-sm font-medium text-gray-700 mb-1">
                    2FA Code (Optional)
                  </span>
                  <input
                    type="text"
                    id="two_fa_code"
                    name="two_fa_code"
                    value={formData.two_fa_code}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-gray-300 bg-white py-3 px-3 text-sm text-gray-900 placeholder-gray-400 focus:border-rose-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    placeholder="Enter 6-digit code"
                    data-testid="2fa-input"
                  />
                </label>
              )}

              {/* 18+ Age Confirmation (Signup only) */}
              {!isLogin && (
                <div className="mt-4">
                  <div className="flex items-start gap-3">
                    <input
                      id="ageConfirmed"
                      type="checkbox"
                      checked={ageConfirmed}
                      onChange={(e) => {
                        setAgeConfirmed(e.target.checked);
                        setAgeTouched(true);
                        if (e.target.checked) setError('');
                      }}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500"
                      data-testid="age-confirm-checkbox"
                    />
                    <label htmlFor="ageConfirmed" className="text-sm text-gray-700 leading-5">
                      I confirm I&apos;m <strong>18 years or older</strong> and I agree to Unity Love&apos;s{' '}
                      <Link to="/terms" className="text-rose-600 hover:underline underline-offset-4">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-rose-600 hover:underline underline-offset-4">
                        Privacy Policy
                      </Link>
                      .
                    </label>
                  </div>
                  {ageTouched && !ageConfirmed ? (
                    <div className="mt-2 text-xs text-red-500">
                      Required to create an account.
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-gray-500">
                      Adults only (18+). Accounts for minors are removed.
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || (!isLogin && !ageConfirmed)}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-btn"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="spinner w-4 h-4 border-2"></div>
                    <span>{isLogin ? 'Signing In...' : 'Creating Account...'}</span>
                  </div>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </button>

              {/* 18+ Legal Note */}
              <div className="mt-3 text-xs text-gray-500 text-center">
                <span className="font-semibold">18+ only.</span> By using Unity Love you agree to our{' '}
                <Link to="/terms" className="hover:underline underline-offset-4">
                  Terms
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="hover:underline underline-offset-4">
                  Privacy Policy
                </Link>
                .
              </div>
            </form>

            {/* Toggle Login/Register */}
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(!isLogin);
                    setError('');
                    setFormData({
                      email: '',
                      password: '',
                      user_type: 'single',
                      two_fa_code: ''
                    });
                    // Reset age confirmation when switching modes
                    setAgeConfirmed(false);
                    setAgeTouched(false);
                  }}
                  className="ml-2 text-rose-600 font-semibold hover:text-rose-700"
                  data-testid="toggle-auth-btn"
                >
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>
          </div>

          {/* Back to home */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-800"
              data-testid="back-home-btn"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1515015520803-1a2e0aadb290?w=800&q=80"
          alt="African American couple"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600/20 to-purple-900/20"></div>
        
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <h3 className="font-playfair text-2xl font-bold mb-2">
            &ldquo;Love knows no boundaries, only connections.&rdquo;
          </h3>
          <p className="text-white/90">
            Join a community that celebrates authentic relationships 
            built on shared values and cultural understanding.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;